import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../AuthContext";
import { ORDER_STATUSES, STATUS_LABEL, STATUS_COLOR } from "../constants";
import { Loader2, Calendar, AlertTriangle, MapPin, Clock, Euro } from "lucide-react";
import { formatDate } from "../utils";

function StatusBreakdown({ data }) {
  return (
    <div className="grid grid-cols-3 gap-2 mt-4">
      {ORDER_STATUSES.filter((s) => s.key !== "cancelled").map((s) => (
        <div key={s.key} className="text-center">
          <div className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded text-xs font-semibold ${s.color}`}>
            {data?.[s.key] ?? 0}
          </div>
          <p className="text-[10px] uppercase tracking-wider text-[#2C2C2C]/50 mt-1 leading-tight">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

function DayCard({ title, subtitle, data, accent, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="text-left bg-white rounded-2xl p-6 border border-[#D4C4A8]/40 shadow-sm hover:shadow-md transition-shadow w-full"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#2C2C2C]/70 uppercase tracking-wider">{title}</h3>
          <p className="text-xs text-[#2C2C2C]/50 mt-0.5">{subtitle}</p>
        </div>
        <Calendar size={18} style={{ color: accent }} />
      </div>
      <div className="flex items-end gap-2">
        <span className="text-5xl font-semibold leading-none" style={{ color: accent }}>
          {data?.total ?? 0}
        </span>
        <span className="text-xs text-[#2C2C2C]/60 pb-1">encomendas</span>
      </div>
      <StatusBreakdown data={data} />
    </button>
  );
}

function FocusList({ title, icon: Icon, items, onNavigate, emptyMsg, testId }) {
  return (
    <div className="bg-white rounded-xl border border-[#D4C4A8]/40 shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#3A4A38]" />
        <h4 className="text-sm font-semibold text-[#2C2C2C]">{title}</h4>
        <span className="ml-auto text-xs px-2 py-0.5 rounded bg-[#F3F1EB] text-[#2C2C2C]/70">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[#2C2C2C]/50 italic">{emptyMsg}</p>
      ) : (
        <ul className="space-y-2 max-h-56 overflow-y-auto">
          {items.slice(0, 8).map((o) => (
            <li key={o.id}>
              <button
                onClick={() => onNavigate(o)}
                className="w-full text-left text-xs flex items-center gap-2 px-2 py-2 rounded hover:bg-[#F3F1EB]/60 transition-colors"
              >
                <span className="font-mono text-[#3A4A38] font-medium">{o.order_number}</span>
                <span className="text-[#2C2C2C]/80 truncate">{o.name}</span>
                <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLOR[o.status]}`}>
                  {STATUS_LABEL[o.status]}
                </span>
                <span className="text-[#2C2C2C]/50 whitespace-nowrap">{o.delivery_date}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SummaryPage({ onNavigateToOrders }) {
  const { authHeaders } = useAuth();
  const [summary, setSummary] = useState(null);
  const [focus, setFocus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { headers: authHeaders() };
      const [s, f] = await Promise.all([
        axios.get(`${API}/admin/dashboard/summary`, headers),
        axios.get(`${API}/admin/dashboard/focus`, headers),
      ]);
      setSummary(s.data);
      setFocus(f.data);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#3A4A38]" />
      </div>
    );
  }

  const goWithFilter = (filter) => onNavigateToOrders?.(filter);

  return (
    <div data-testid="admin-summary-page" className="space-y-8">
      {/* Three operational cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DayCard
          title="Hoje"
          subtitle={summary.today.date}
          data={summary.today}
          accent="#3A4A38"
          testId="summary-card-today"
          onClick={() => goWithFilter({ delivery_from: summary.today.date, delivery_to: summary.today.date })}
        />
        <DayCard
          title="Amanhã"
          subtitle={summary.tomorrow.date}
          data={summary.tomorrow}
          accent="#1B93A4"
          testId="summary-card-tomorrow"
          onClick={() => goWithFilter({ delivery_from: summary.tomorrow.date, delivery_to: summary.tomorrow.date })}
        />
        <DayCard
          title="Próximos 3 dias"
          subtitle={`${summary.next_three_days.from} → ${summary.next_three_days.to}`}
          data={summary.next_three_days}
          accent="#B07F2B"
          testId="summary-card-3days"
          onClick={() => goWithFilter({ delivery_from: summary.next_three_days.from, delivery_to: summary.next_three_days.to })}
        />
      </div>

      {/* Operational focus */}
      <section>
        <h2 className="text-lg font-semibold text-[#2C2C2C] mb-4">Foco operacional</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FocusList
            title="Próximas encomendas a preparar"
            icon={Clock}
            items={focus?.to_prepare || []}
            onNavigate={(o) => goWithFilter({ status: o.status, _highlight: o.id })}
            emptyMsg="Nada por preparar para já."
            testId="focus-to-prepare"
          />
          <FocusList
            title="Encomendas de amanhã ainda Novas"
            icon={AlertTriangle}
            items={focus?.tomorrow_unconfirmed || []}
            onNavigate={(o) => goWithFilter({ status: "new", delivery_from: summary.tomorrow.date, delivery_to: summary.tomorrow.date, _highlight: o.id })}
            emptyMsg="Todas as encomendas de amanhã confirmadas."
            testId="focus-tomorrow-unconfirmed"
          />
          <FocusList
            title="Encomendas sem rota atribuída"
            icon={MapPin}
            items={focus?.no_route || []}
            onNavigate={(o) => goWithFilter({ route_id: "none", _highlight: o.id })}
            emptyMsg="Todas as encomendas dos próximos 3 dias têm rota."
            testId="focus-no-route"
          />
          <FocusList
            title="Encomendas com morada incompleta"
            icon={AlertTriangle}
            items={focus?.incomplete_address || []}
            onNavigate={(o) => goWithFilter({ _highlight: o.id })}
            emptyMsg="Todas as moradas estão completas."
            testId="focus-incomplete-address"
          />
          <FocusList
            title="Entregues a aguardar pagamento"
            icon={Euro}
            items={focus?.unpaid_delivered || []}
            onNavigate={(o) => goWithFilter({ payment_status: "pending", _highlight: o.id })}
            emptyMsg="Sem pagamentos em atraso."
            testId="focus-unpaid-delivered"
          />
        </div>
      </section>

      <p className="text-[11px] text-[#2C2C2C]/40">
        Última atualização: {formatDate(new Date().toISOString())}
      </p>
    </div>
  );
}
